import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import WebhookSettingsPanel from '../components/WebhookSettingsPanel';
import { authService } from '../services/api';
import { UserSettings } from '../types';

const THEME_OPTIONS = [
  { value: 'light', label: 'Light mode' },
  { value: 'dark', label: 'Dark mode' },
  { value: 'storm', label: 'Storm mode' },
  { value: 'midnight', label: 'Midnight mode' },
  { value: 'emerald', label: 'Emerald mode' },
  { value: 'contrast', label: 'High contrast' },
] as const;

type ThemeOption = typeof THEME_OPTIONS[number]['value'];

const DEFAULT_SETTINGS: UserSettings = {
  theme: 'dark',
  time_format: '12h',
  timezone: 'America/Chicago',
  alert_sound_enabled: true,
  desktop_notifications_enabled: false,
  active_alerts_compact: false,
  show_polygon_data_by_default: false,
};

function getStoredTheme(): ThemeOption {
  const storedTheme = localStorage.getItem('glsn-theme') as ThemeOption | null;
  return THEME_OPTIONS.some((option) => option.value === storedTheme) ? storedTheme! : DEFAULT_SETTINGS.theme;
}

const SettingsPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [settings, setSettings] = useState<UserSettings>({ ...DEFAULT_SETTINGS, theme: getStoredTheme() });
  const [loading, setLoading] = useState(isAuthenticated);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const browserZones = useMemo(() => {
    const intlAny = Intl as any;
    const list = typeof intlAny.supportedValuesOf === 'function' ? intlAny.supportedValuesOf('timeZone') : [];
    return ['America/Chicago', 'America/Detroit', 'America/Indiana/Indianapolis', 'America/New_York', ...list]
      .filter((value, index, arr) => arr.indexOf(value) === index)
      .slice(0, 120);
  }, []);

  useEffect(() => {
    document.body.dataset.theme = settings.theme;
  }, [settings.theme]);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    authService
      .getSettings()
      .then((res) => {
        const next = { ...DEFAULT_SETTINGS, ...res.data.settings };
        const preferredTheme = getStoredTheme();
        const effectiveTheme = preferredTheme && THEME_OPTIONS.some((option) => option.value === preferredTheme)
          ? preferredTheme
          : next.theme;
        const mergedSettings = { ...next, theme: effectiveTheme };
        setSettings(mergedSettings);
        localStorage.setItem('glsn-theme', effectiveTheme);
        document.body.dataset.theme = effectiveTheme;
        window.dispatchEvent(new Event('glsn-theme-change'));
      })
      .catch(() => setError('Unable to load your saved settings.'))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const handleThemeChange = (newTheme: ThemeOption) => {
    localStorage.setItem('glsn-theme', newTheme);
    setSettings((current) => ({ ...current, theme: newTheme }));
    document.body.dataset.theme = newTheme;
    window.dispatchEvent(new Event('glsn-theme-change'));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const res = await authService.updateSettings(settings);
      const next = { ...DEFAULT_SETTINGS, ...res.data.settings };
      const persistedTheme = getStoredTheme();
      const effectiveTheme = persistedTheme && THEME_OPTIONS.some((option) => option.value === persistedTheme)
        ? persistedTheme
        : next.theme;
      const mergedSettings = { ...next, theme: effectiveTheme };
      setSettings(mergedSettings);
      localStorage.setItem('glsn-theme', effectiveTheme);
      document.body.dataset.theme = effectiveTheme;
      window.dispatchEvent(new Event('glsn-theme-change'));
      setMessage('Settings saved.');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-shell container">
      <div className="card settings-card">
        <div className="page-intro">
          <div>
            <p className="eyebrow">User settings</p>
            <h2 className="page-heading">Customize your dashboard</h2>
            <p className="page-copy">Persisted settings are tied to your account and follow you across devices.</p>
          </div>
          {isAuthenticated && <Link to="/account" className="btn btn-secondary">Account & password</Link>}
        </div>

        {!isAuthenticated ? (
          <div className="auth-panel">
            <h3 className="auth-heading">Sign in required</h3>
            <p className="auth-copy">Please sign in to update your GLSN settings.</p>
          </div>
        ) : loading ? (
          <div className="card">Loading settings...</div>
        ) : (
          <div className="simple-stack">
            {error && <div className="alert alert-error">{error}</div>}
            {message && <div className="alert alert-success">{message}</div>}

            <div className="module-card module-card-primary">
              <h3 className="section-heading">Theme selection</h3>
              <div className="theme-option-grid">
                {THEME_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`theme-option ${settings.theme === option.value ? 'theme-selected' : ''}`}
                    onClick={() => handleThemeChange(option.value)}
                  >
                    <span>{option.label}</span>
                    {settings.theme === option.value && <span className="theme-check">Selected</span>}
                  </button>
                ))}
              </div>
              <p className="field-help">Theme preview updates instantly and is also saved to your account profile.</p>
            </div>

            <div className="module-card">
              <h3 className="section-heading">Time and alerts</h3>
              <div className="form-grid two-column">
                <div className="form-group">
                  <label className="form-label" htmlFor="time-format">Time format</label>
                  <select
                    id="time-format"
                    className="form-input"
                    value={settings.time_format}
                    onChange={(event) => setSettings((current) => ({ ...current, time_format: event.target.value as '12h' | '24h' }))}
                  >
                    <option value="12h">12 hour</option>
                    <option value="24h">24 hour</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="timezone">Timezone</label>
                  <select
                    id="timezone"
                    className="form-input"
                    value={settings.timezone}
                    onChange={(event) => setSettings((current) => ({ ...current, timezone: event.target.value }))}
                  >
                    {browserZones.map((zone) => (
                      <option key={zone} value={zone}>{zone}</option>
                    ))}
                  </select>
                </div>

                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={settings.alert_sound_enabled}
                    onChange={(event) => setSettings((current) => ({ ...current, alert_sound_enabled: event.target.checked }))}
                  />
                  Enable alert sound
                </label>

                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={settings.desktop_notifications_enabled}
                    onChange={(event) => setSettings((current) => ({ ...current, desktop_notifications_enabled: event.target.checked }))}
                  />
                  Enable desktop notifications
                </label>

                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={settings.active_alerts_compact}
                    onChange={(event) => setSettings((current) => ({ ...current, active_alerts_compact: event.target.checked }))}
                  />
                  Compact active-alert card layout
                </label>

                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={settings.show_polygon_data_by_default}
                    onChange={(event) => setSettings((current) => ({ ...current, show_polygon_data_by_default: event.target.checked }))}
                  />
                  Expand polygon data by default
                </label>
              </div>

              <div className="action-row" style={{ marginTop: 10 }}>
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save settings'}
                </button>
              </div>
            </div>
            {user?.role === 'owner' && <WebhookSettingsPanel />}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
